import { billingService } from "@/services";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface InvoiceStatus {
  success: boolean;
  isPaid: boolean;
  status: string;
  invoiceNumber: string;
  paidAt: string | null;
}

interface BillingContextType {
  isProcessing: boolean;
  error: string | null;
  manageBilling: () => Promise<void>;
  payDeposit: (serviceOrderId: string, invoiceId: string) => Promise<void>;
  payInvoice: (invoiceId: string) => Promise<void>;
  checkInvoiceStatus: (invoiceId: string) => Promise<InvoiceStatus | null>;
}

const BillingContext = createContext<BillingContextType | null>(null);

export function BillingProvider({ children }: { children: ReactNode }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const manageBilling = useCallback(async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const { url } = await billingService.manageBilling();
      if (url) {
        window.open(url, "_blank");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to open Stripe portal";
      setError(errorMessage);
      console.error("Failed to open Stripe portal:", err);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const payDeposit = useCallback(
    async (serviceOrderId: string, invoiceId: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        const { url } = await billingService.payDeposit(
          serviceOrderId,
          invoiceId
        );
        if (url) {
          window.location.href = url;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to create deposit checkout";
        setError(errorMessage);
        console.error("Failed to create deposit checkout:", err);
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const payInvoice = useCallback(async (invoiceId: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const { url } = await billingService.payInvoice(invoiceId);
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to create invoice checkout";
      setError(errorMessage);
      console.error("Failed to create invoice checkout:", err);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const checkInvoiceStatus = useCallback(
    async (invoiceId: string): Promise<InvoiceStatus | null> => {
      setError(null);
      try {
        return await billingService.checkInvoiceStatus(invoiceId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to check invoice status";
        setError(errorMessage);
        console.error("Failed to check invoice status:", err);
        return null;
      }
    },
    []
  );

  return (
    <BillingContext.Provider
      value={{
        isProcessing,
        error,
        manageBilling,
        payDeposit,
        payInvoice,
        checkInvoiceStatus,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error("useBilling must be used within a BillingProvider");
  }
  return context;
}
